// Phase 83y.7 — Single source of truth for event format detection.
//
// Background
// ──────────
// The event document stored in MongoDB has THREE fields that overlap in
// meaning: `discipline` ∈ {singles, doubles, mixed_doubles (legacy)},
// `gender` ∈ {male, female, mixed, mixed_pair}, and `event_type` (legacy
// composite key like "men_doubles" / "mixed_doubles"). Different parts
// of the app used to read different combinations and the schemas drifted
// apart over time, producing bugs like:
//   • Wizard saves `discipline=doubles, gender=mixed_pair` for "Đôi nam nữ"
//   • Athlete registration filter only checked `discipline === "mixed_doubles"`
//   • → format inference returned null → UI rendered "Đơn — Không cần đồng đội"
//
// This module is the ONE PLACE that decides "what kind of event is this?".
// Everyone (admin wizard, athlete registration wizard, public list, receipt)
// must import from here.

// ──────────────────────────────────────────────────────────────────
// The 5 formats the UI supports.
// ──────────────────────────────────────────────────────────────────
export const EVENT_FORMAT = {
  SINGLES_MALE:        "singles_male",
  SINGLES_FEMALE:      "singles_female",
  DOUBLES_MALE:        "doubles_male",
  DOUBLES_FEMALE:      "doubles_female",
  MIXED_DOUBLES:       "mixed_doubles",        // Đôi nam nữ (strict 1M+1F)
  // Phase 84.M.48 — split "Đôi hỗn hợp" (mọi tổ hợp giới tính) khỏi "Đôi nam nữ".
  MIXED_DOUBLES_FREE:  "mixed_doubles_free",   // Đôi hỗn hợp (free)
};

const ORDER = [
  EVENT_FORMAT.MIXED_DOUBLES,
  EVENT_FORMAT.MIXED_DOUBLES_FREE,
  EVENT_FORMAT.DOUBLES_MALE,
  EVENT_FORMAT.DOUBLES_FEMALE,
  EVENT_FORMAT.SINGLES_MALE,
  EVENT_FORMAT.SINGLES_FEMALE,
];

const META = {
  [EVENT_FORMAT.SINGLES_MALE]: {
    id: EVENT_FORMAT.SINGLES_MALE,
    label: "Đơn nam",
    short: "Đơn nam",
    discipline: "singles",
    gender: "male",
    event_type: "men_singles",
    minTeammates: 0,
    maxTeammates: 0,
    tag: "bg-[#3B82F6]/20 text-[#60A5FA]",
  },
  [EVENT_FORMAT.SINGLES_FEMALE]: {
    id: EVENT_FORMAT.SINGLES_FEMALE,
    label: "Đơn nữ",
    short: "Đơn nữ",
    discipline: "singles",
    gender: "female",
    event_type: "women_singles",
    minTeammates: 0,
    maxTeammates: 0,
    tag: "bg-[#EC4899]/20 text-[#F9A8D4]",
  },
  [EVENT_FORMAT.DOUBLES_MALE]: {
    id: EVENT_FORMAT.DOUBLES_MALE,
    label: "Đôi nam",
    short: "Đôi nam",
    discipline: "doubles",
    gender: "male",
    event_type: "men_doubles",
    minTeammates: 1,
    maxTeammates: 1,
    tag: "bg-[#3B82F6]/20 text-[#60A5FA]",
  },
  [EVENT_FORMAT.DOUBLES_FEMALE]: {
    id: EVENT_FORMAT.DOUBLES_FEMALE,
    label: "Đôi nữ",
    short: "Đôi nữ",
    discipline: "doubles",
    gender: "female",
    event_type: "women_doubles",
    minTeammates: 1,
    maxTeammates: 1,
    tag: "bg-[#EC4899]/20 text-[#F9A8D4]",
  },
  [EVENT_FORMAT.MIXED_DOUBLES]: {
    id: EVENT_FORMAT.MIXED_DOUBLES,
    label: "Đôi nam nữ",
    short: "Đôi nam nữ",
    discipline: "doubles",
    gender: "mixed_pair",
    event_type: "mixed_doubles",
    minTeammates: 1,
    maxTeammates: 1,
    tag: "bg-[#F59E0B]/20 text-[#FBBF24]",
  },
  // Phase 84.M.48 — Đôi hỗn hợp (free, không validate giới tính).
  // Backend `_infer_event_format` map cái này về `custom` để bỏ qua
  // validate; FE coi như nội dung "tự do" — captain + partner có thể là
  // bất kỳ giới tính nào (2M, 2F, 1M+1F).
  [EVENT_FORMAT.MIXED_DOUBLES_FREE]: {
    id: EVENT_FORMAT.MIXED_DOUBLES_FREE,
    label: "Đôi hỗn hợp",
    short: "Đôi hỗn hợp",
    discipline: "doubles",
    gender: "mixed",
    event_type: "custom",
    minTeammates: 1,
    maxTeammates: 1,
    tag: "bg-[#10B981]/20 text-[#34D399]",
  },
};

// ──────────────────────────────────────────────────────────────────
// PUBLIC API
// ──────────────────────────────────────────────────────────────────

/**
 * Detect an event's format from any combination of {discipline, gender,
 * event_type}. Tolerates the legacy schema (`discipline="mixed_doubles"`)
 * and the production schema (`discipline="doubles" + gender="mixed_pair"`).
 *
 * @param {object} ev raw event from the API (or from the wizard state).
 * @returns {string|null}  one of EVENT_FORMAT values, or null if unknowable.
 */
export function getEventFormatId(ev) {
  if (!ev) return null;
  // Phase 84.M.48 — Khi event_type là canonical "custom" mà có discipline
  // doubles + gender "mixed" → đây là "Đôi hỗn hợp". Detect SỚM trước khi
  // fall vào nhánh event_type chuẩn để không bị wizard ghi đè bởi compose
  // logic cũ (legacy data có event_type="mixed_doubles" + gender="mixed").
  const dEarly = ev.discipline;
  const gEarly = ev.gender;
  if (dEarly === "doubles" && gEarly === "mixed") {
    return EVENT_FORMAT.MIXED_DOUBLES_FREE;
  }
  // Prefer the explicit event_type column when set — it's the most reliable
  // indicator written by the admin wizard.
  const t = ev.event_type;
  if (t === "men_singles")    return EVENT_FORMAT.SINGLES_MALE;
  if (t === "women_singles")  return EVENT_FORMAT.SINGLES_FEMALE;
  if (t === "men_doubles")    return EVENT_FORMAT.DOUBLES_MALE;
  if (t === "women_doubles")  return EVENT_FORMAT.DOUBLES_FEMALE;
  if (t === "mixed_doubles")  return EVENT_FORMAT.MIXED_DOUBLES;

  // Fall back to discipline + gender. Cover BOTH schemas:
  //   • Legacy: `discipline === "mixed_doubles"` (no gender field).
  //   • Production: `discipline === "doubles" && gender === "mixed_pair"`.
  // Phase 84.M.48: `gender === "mixed"` đã được handle ở nhánh sớm bên trên.
  const d = ev.discipline;
  const g = ev.gender;
  if (d === "mixed_doubles") return EVENT_FORMAT.MIXED_DOUBLES;
  if (d === "doubles") {
    if (g === "male")        return EVENT_FORMAT.DOUBLES_MALE;
    if (g === "female")      return EVENT_FORMAT.DOUBLES_FEMALE;
    if (g === "mixed_pair")  return EVENT_FORMAT.MIXED_DOUBLES;
  }
  if (d === "singles") {
    if (g === "male")   return EVENT_FORMAT.SINGLES_MALE;
    if (g === "female") return EVENT_FORMAT.SINGLES_FEMALE;
  }
  return null;
}

/**
 * Human-readable Vietnamese label for an event. Returns "Tuỳ chỉnh" when
 * the format can't be inferred (rare — happens for custom configurations
 * the admin builds outside the standard 5 buckets).
 */
export function getEventFormatLabel(ev) {
  const id = getEventFormatId(ev);
  return id ? META[id].label : "Tuỳ chỉnh";
}

/**
 * Get the full meta record (label, minTeammates, tag, …) for an event.
 * Returns null when the event doesn't match any known format.
 */
export function getEventFormatMeta(ev) {
  const id = getEventFormatId(ev);
  return id ? META[id] : null;
}

/**
 * Inverse of `getEventFormatId`: given a format id, return the field
 * triple {discipline, gender, event_type} the admin wizard must write.
 * Use this so the wizard never invents new combinations the rest of the
 * app can't recognise.
 */
export function composeEventFromFormat(formatId) {
  const meta = META[formatId];
  if (!meta) return null;
  return {
    discipline: meta.discipline,
    gender: meta.gender,
    event_type: meta.event_type,
  };
}

/** All 5 supported formats in display order — for selector UIs. */
export function listEventFormats() {
  return ORDER.map((id) => META[id]);
}
